// ════════════════════════════════════════════════════════════════════════════
//  ManDana — the lens
//  ---------------------------------------------------------------------------
//  How somebody has chosen to LOOK at a set of things: as a list, as a board of
//  columns, or as spans on a timeline — with what filtered out, what sorted by,
//  what grouped into columns, and which fields on show.
//
//  ── THIS FILE KNOWS NOTHING ABOUT WHAT IT IS LOOKING AT ───────────────────
//  It was extracted from md-workshop.ts, which grew the whole of it while
//  looking at projects. Almost none of it was ever about projects. Six things
//  were — what fields there are, which of them make columns, where the rows
//  come from, what one cell reads as, what buckets an axis declares, and what
//  the empty bucket is called — and those six are now a CATALOGUE, handed in.
//
//  So the Workshop is one catalogue over this lens, a project's own records are
//  a second, and a third costs whoever writes it those six answers and not a
//  line of filtering, sorting, grouping, sanitising, storing or memoising.
//
//  ── PRESENTATION, AND ONLY PRESENTATION ───────────────────────────────────
//  Not one project, record or row lives here. A view is a lens; deleting every
//  view destroys nothing, and a device that has never written one still sees
//  everything it has. That is the line md-project.ts and md-project-record.ts
//  are on the other side of, and it is why this file can be as opinionated as
//  it likes about shape.
//
//  ── ONE DOCUMENT PER SCOPE ────────────────────────────────────────────────
//  A catalogue may be SCOPED: the Workshop has one set of views for the whole
//  account (no scope), while a project's records have one set per project (the
//  project's id is the scope). The key and the sync document take the scope as
//  a suffix, so the storage unit is the sync unit either way — the law every
//  document in this app is under.
//
//  A view arriving from a NEWER build keeps the parts this one does not
//  understand (the R-3 rule): fields are laid over a sanitised base rather than
//  rebuilt from it, so an old device does not quietly undo a new one.
//
//  ── THE RULES, INHERITED FROM md-database.ts:1892-1990 ────────────────────
//  Because two screens that group differently are two apps:
//    • every value of a fixed axis is a group, whether or not anything is in it
//      — "a column you cannot drop onto is a broken promise";
//    • the empty bucket is always LAST, and it is named rather than blank;
//    • declared order beats alphabetical: a ladder somebody reads is not a
//      thing to file.
//  And one this file adds: EMPTIES SORT LAST IN BOTH DIRECTIONS. An empty is
//  not the smallest value, it is the absence of one. Reversing a sort must not
//  promote everything nobody has answered to the top of the screen.
// ════════════════════════════════════════════════════════════════════════════

type MdViewType = 'list' | 'board' | 'timeline';
type MdViewZoom = 'day' | 'week' | 'month' | 'quarter';
type MdViewOperator = 'is' | 'is_not' | 'contains' | 'not_contains' | 'empty' | 'not_empty'
  | 'before' | 'after' | 'on_or_before' | 'on_or_after';

interface MdViewSort { field: string; direction: 'asc' | 'desc' }
/* `value` may be a scalar or an ARRAY of scalars. An array under `is` reads
   "is any of"; under `is_not`, "is none of". One filter, several answers —
   which is the question a picker with checkboxes actually asks. */
interface MdViewFilter { field: string; operator: MdViewOperator; value?: unknown }

interface MdView {
  id: string;
  name: string;
  /* What the view is FOR, in its keeper's words — optional by design, and
     honest about it: the default is empty, never boilerplate. */
  description: string;
  type: MdViewType;
  groupBy: string | null;
  groupDirection: 'asc' | 'desc';
  sorts: MdViewSort[];
  filters: MdViewFilter[];
  /* How the filters are read together. ONE join for the whole set, not one
     per condition: nested groups are a query builder, and the question a
     screen is actually asked is "all of these" or "any of these". */
  conjunction: 'and' | 'or';
  visibleFields: string[];
  /* The hand-made order, a HINT rather than a ledger: row ids this view has
     placed by hand. It speaks only when no sort does; ids it does not list
     append in the order they were begun, and ids that no longer name a row
     are ignored — so a stale array degrades to the old behaviour instead of
     breaking the list. */
  order: string[];
  showClosed: boolean;
  showEmptyGroups: boolean;
  timelineZoom: MdViewZoom;
  search: string;
  /* THE LENS — which part of a life this is being read through, not a question.
     `null` is everything; otherwise the ids it is pointed at, with `'empty'`
     standing for the rows under none of them.

     It is deliberately NOT a filter, and the difference is load-bearing: a
     filter is a question somebody composed and kept, joined to the others by
     `conjunction`, and stored with the view. The lens is switched twenty times
     a day, it gates AHEAD of the filters and outside the join — an `or` view
     narrowed to one pursuit must not widen back out — and the surface hands it
     in transiently, the way it hands in a typed search.

     Not to be read as the `scope` a catalogue's views are KEPT under (see
     `keyFor`): that one names which set of views this is, and never travels
     inside one. Same word, two altitudes; only this one is a view's own. */
  scope: string[] | null;
}

/** The six answers a catalogue owes, plus where it keeps its views. */
interface MdViewsCatalogue {
  id: string;
  /** Storage key base. A scope is appended as `:scope`. */
  key: string;
  /** Sync document name base. A scope is appended the same way. */
  doc: string;
  fields: string[];
  /** Only these make columns. A date does not: a board grouped by a date is a
      column per day, which is a timeline drawn badly. */
  groupBy: string[];
  /** Fields whose values have a declared order somebody reads, not an
      alphabet. Keyed by field; the array is the ladder. */
  declared?: Record<string, string[] | (() => string[])>;
  /** Fields ranked by a function of the ROW rather than by a ladder of values.
      A field whose order is not global has no list to be indexed into — a
      status belonging to one of many workflows is the case this exists for,
      since status ids repeat across workflows and mean different places in
      each. Takes precedence over `declared` for the same field. */
  rank?: Record<string, (row: any) => number>;
  /** Fields compared as numbers rather than as text. */
  numeric?: string[];
  zooms?: MdViewZoom[];
  /** The buckets of one axis, IN ORDER, whether or not anything is in them.
      Handed the rows IN VIEW, because an axis can be a question about them: a
      board holding one workflow draws that workflow's statuses, and a board
      holding two has to fall back to what they agree on. Most axes ignore the
      second argument entirely. */
  buckets: (axis: string, rows: any[], ctx?: any) => { id: string; label: string }[];
  /** Which bucket a row belongs in, when that is not simply its cell — the
      other half of an axis that answers to what is in view. Returning
      `undefined` means "the ordinary answer"; `null` means the empty bucket. */
  bucketOf?: (row: any, axis: string, rows: any[]) => unknown;
  /** What the empty bucket of one axis is called. An axis with no entry here
      HAS no empty bucket: a column nothing can ever land in is the broken
      promise inverted, and an axis whose cell is never absent must not grow
      one. */
  emptyLabel?: Record<string, string>;
  /** What every account has without anybody writing anything. The FIRST one is
      what the screen opens on. */
  defaults: () => unknown[];
  rows: (ctx: any) => any[];
  /** What one field of one row reads as. `null` is the one and only empty. */
  cell: (row: any, field: string) => unknown;
  /** Rows the DISPLAY withholds — the closed, with Show closed off. Not the
      asking: nobody asked these away, so they are never in the hidden count. */
  hidden?: (row: any, view: MdView) => boolean;
  /** What the typed search reads. The name, unless a catalogue says otherwise. */
  search?: (row: any) => string;
  /** The parts of ctx whose IDENTITY decides whether a memo still holds. */
  memo?: (ctx: any) => unknown[];
  /** What the LENS reads off a row — the id it must match, or `'empty'`.
      A catalogue that does not declare this has no lens, and a `scope` stored
      on one of its views is carried without ever being obeyed. */
  lensOf?: (row: any) => string;
  /** Which grouping axis the lens is the same question as. On that axis alone
      the lens may take a bucket away: a column for something set aside is the
      broken promise inverted — a place nothing can ever land. */
  lensAxis?: string;
  /** What a view shows when nobody has said otherwise. Defaults to every
      field; a catalogue narrows it when a column is one the engine holds and a
      table should not draw unasked. */
  defaultFields?: string[];
  /** This catalogue's document version, and the one-way repair that carries a
      document written before it. A read stays a read: the stamp lands on the
      next write, and a stamped document is never migrated again. */
  version?: number;
  migrate?: (views: MdView[], from: number) => MdView[];
}

(function (g: any) {
  'use strict';

  const NAME_MAX = 60;
  const DESCRIPTION_MAX = 240;
  const MAX_VIEWS = 24;
  const MAX_SORTS = 8;
  const MAX_FILTERS = 16;
  /* Answers one filter may hold. Sixteen, like the filters themselves: past
     any hand-picked set, and small enough that a hostile document cannot
     grow a views file without bound. */
  const MAX_ANSWERS = 16;
  /* Well past any list a hand could plausibly have placed, and small enough
     that a hostile document cannot grow a views file without bound. */
  const MAX_ORDER = 500;
  /* Ids one lens may hold. Well past any life's worth of striving, and bounded
     for the same reason everything else here is. */
  const MAX_LENS = 100;

  const VIEW_TYPES: MdViewType[] = ['list', 'board', 'timeline'];
  const ZOOMS: MdViewZoom[] = ['day', 'week', 'month', 'quarter'];
  const OPERATORS: MdViewOperator[] = ['is', 'is_not', 'contains', 'not_contains',
    'empty', 'not_empty', 'before', 'after', 'on_or_before', 'on_or_after'];
  const OPERATOR_SET = new Set<string>(OPERATORS);

  const catalogues = new Map<string, MdViewsCatalogue>();

  function mdViewsRegister(value: unknown): boolean {
    if (!value || typeof value !== 'object') return false;
    const cat = value as MdViewsCatalogue;
    if (typeof cat.id !== 'string' || !cat.id) return false;
    if (typeof cat.key !== 'string' || typeof cat.doc !== 'string') return false;
    if (!Array.isArray(cat.fields) || !cat.fields.length) return false;
    if (typeof cat.rows !== 'function' || typeof cat.cell !== 'function') return false;
    catalogues.set(cat.id, cat);
    return true;
  }

  function mdViewsCatalogue(id: unknown): MdViewsCatalogue | null {
    return typeof id === 'string' ? catalogues.get(id) || null : null;
  }

  /* ── the scope ────────────────────────────────────────────────────────────
     A scope names WHICH set of views: absent for a catalogue that has one for
     the whole account, present for one that keeps a set per thing. It is
     shaped like an id because it becomes part of a storage key and part of a
     sync document name, and neither of those may carry whatever arrives. */
  function scopeOf(value: unknown): string | null {
    return typeof value === 'string' && /^[a-z0-9_-]{1,64}$/i.test(value) ? value : null;
  }
  const keyFor = (cat: MdViewsCatalogue, scope: unknown): string => {
    const s = scopeOf(scope);
    return s ? `${cat.key}:${s}` : cat.key;
  };
  const docFor = (cat: MdViewsCatalogue, scope: unknown): string => {
    const s = scopeOf(scope);
    return s ? `${cat.doc}:${s}` : cat.doc;
  };

  function validId(value: unknown): value is string {
    return typeof value === 'string' && /^[a-z0-9]{6,40}$/.test(value);
  }

  function newId(): string {
    let s = 'v';
    while (s.length < 10) s += Math.random().toString(36).slice(2).replace(/[^a-z0-9]/g, '');
    return s.slice(0, 10);
  }

  function text(value: unknown, max: number): string {
    return typeof value === 'string' ? value.trim().slice(0, max) : '';
  }

  /** One view, structurally sanitised against its catalogue's field set.
      Nothing else is read to do it — a fixed field set is the whole difference
      from a database's view, which cannot be checked without its parent
      schema. A filter naming a field this catalogue has never heard of is
      simply dropped. */
  function mdViewsSanitise(catId: unknown, value: unknown): MdView | null {
    const cat = mdViewsCatalogue(catId);
    if (!cat) return null;
    if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
    const raw = value as Record<string, any>;
    const type: MdViewType = VIEW_TYPES.indexOf(raw.type) === -1 ? 'list' : raw.type;
    const FIELD_SET = new Set<string>(cat.fields);
    const zooms = Array.isArray(cat.zooms) && cat.zooms.length ? cat.zooms : ZOOMS;
    const first = cat.fields[0]!;

    const fields = Array.isArray(raw.visibleFields)
      ? raw.visibleFields.filter((f: unknown): f is string => typeof f === 'string' && FIELD_SET.has(f))
      : [];
    /* An empty list means "everything", not "nothing" — a row with no columns
       is not a row. And the first field is never optional whatever the
       document says: without it the rows cannot be told apart or opened. */
    const shown = Array.isArray(cat.defaultFields) && cat.defaultFields.length
      ? cat.defaultFields : cat.fields;
    const visibleFields: string[] = fields.length
      ? (fields.indexOf(first) === 0 ? fields : [first, ...fields.filter((f) => f !== first)])
      : [...shown];

    const sorts: MdViewSort[] = (Array.isArray(raw.sorts) ? raw.sorts : [])
      .filter((s: any) => s && FIELD_SET.has(s.field) && (s.direction === 'asc' || s.direction === 'desc'))
      .slice(0, MAX_SORTS)
      .map((s: any) => ({ field: s.field, direction: s.direction }));

    /* An array value is sanitised ENTRY BY ENTRY — only scalars survive, each
       said once, capped — and a filter whose answers all fall out falls out
       whole: a question with no answers left is not a question, and dropping
       it can only NARROW nothing (an empty any-of matches no row at all).
       A scalar value passes through untouched, so yesterday's documents mean
       today exactly what they meant. */
    const answers = (held: unknown): unknown => {
      if (!Array.isArray(held)) return held;
      const seen = new Set<string>();
      const kept: unknown[] = [];
      for (const one of held) {
        if (typeof one !== 'string' && typeof one !== 'number') continue;
        const key = String(one);
        if (seen.has(key)) continue;
        seen.add(key);
        kept.push(one);
        if (kept.length >= MAX_ANSWERS) break;
      }
      return kept;
    };
    const filters: MdViewFilter[] = (Array.isArray(raw.filters) ? raw.filters : [])
      .filter((f: any) => f && FIELD_SET.has(f.field) && OPERATOR_SET.has(f.operator))
      .map((f: any) => ({ field: f.field, operator: f.operator, value: answers(f.value) }))
      .filter((f: MdViewFilter) => !(Array.isArray(f.value) && !f.value.length))
      .slice(0, MAX_FILTERS);

    /* An order is ids or it is nothing — without this clause the `...raw`
       spread below would store whatever JSON arrived under the name. Shaped
       like an id, each at most once, capped. */
    const order: string[] = [];
    if (Array.isArray(raw.order)) {
      const seen = new Set<string>();
      for (const id of raw.order) {
        if (typeof id !== 'string' || !/^[a-z0-9_-]{1,64}$/i.test(id) || seen.has(id)) continue;
        seen.add(id);
        order.push(id);
        if (order.length >= MAX_ORDER) break;
      }
    }

    /* The lens, sanitised by the same law the order is: ids or nothing. An
       EMPTY list is no lens at all, not a lens onto nothing — the rule the
       filters already keep ("no filters is no filter"), and the one that stops
       a control from producing a guaranteed-blank screen. */
    let scope: string[] | null = null;
    if (Array.isArray(raw.scope)) {
      const seen = new Set<string>();
      const kept: string[] = [];
      for (const id of raw.scope) {
        if (typeof id !== 'string' || !/^[a-z0-9_-]{1,64}$/i.test(id) || seen.has(id)) continue;
        seen.add(id);
        kept.push(id);
        if (kept.length >= MAX_LENS) break;
      }
      scope = kept.length ? kept : null;
    }

    /* The R-3 rule: laid OVER the incoming object rather than built from
       scratch, so a field a newer build added survives a round trip through
       this one. Everything this build knows about is then overwritten with the
       sanitised value, so nothing unchecked can wear a known name. */
    return {
      ...raw,
      id: validId(raw.id) ? raw.id : newId(),
      name: text(raw.name, NAME_MAX) || type.charAt(0).toUpperCase() + type.slice(1),
      description: text(raw.description, DESCRIPTION_MAX),
      type,
      groupBy: cat.groupBy.indexOf(raw.groupBy) === -1 ? null : raw.groupBy,
      groupDirection: raw.groupDirection === 'desc' ? 'desc' : 'asc',
      sorts,
      filters,
      order,
      visibleFields,
      /* `and` is the fallback, and deliberately so: it is what every view did
         before this field existed, so an older document — or a word a newer
         build invented — can never silently WIDEN somebody's filters into
         showing more than they asked to see. */
      conjunction: raw.conjunction === 'or' ? 'or' : 'and',
      showClosed: raw.showClosed === true,
      showEmptyGroups: raw.showEmptyGroups !== false,
      timelineZoom: zooms.indexOf(raw.timelineZoom) === -1 ? 'month' : raw.timelineZoom,
      search: text(raw.search, 120),
      scope,
    };
  }

  /* ── the document ─────────────────────────────────────────────────────────
     THE DEFAULTS ARE MATERIALISED, NOT SEEDED. They are not written at first
     launch; they are what this file returns when the document is absent. So an
     account that has never opened a screen already has them, an account
     syncing from an older device cannot lose them, and their names travel with
     the code rather than with the data.

     Once anything IS written, the stored list is the truth — including a
     shorter one. Deleting a view you do not want must not be undone on the
     next read. What is refused is deleting the LAST one: a screen with no view
     is no screen. */

  interface MdViewsDoc { views: MdView[]; activeId: string | null }

  /* ── the version stamp ────────────────────────────────────────────────────
     A default only reaches views nobody has written. Every view somebody has
     so much as regrouped is stored with its fields listed out, so a change to
     what a view shows by default would announce itself on a fresh account and
     nowhere else — which is the shape of "we changed it" that reads as "it did
     not work". A catalogue that needs to carry those documents forward
     declares a `version` and the one-way repair that reaches it.

     Nothing is written eagerly: a read stays a read, the stamp lands on the
     next write, and a stamped document is never migrated again. Incoming sync
     documents go through the same door, because a device on an older build
     sends v-less views, and stamping those unmigrated would make that device
     the one that decides. */
  const versionOf = (raw: any): number =>
    (raw && typeof raw.v === 'number' && Number.isFinite(raw.v) ? raw.v : 0);

  const carried = (cat: MdViewsCatalogue, views: MdView[], raw: any): MdView[] =>
    (typeof cat.migrate === 'function' ? cat.migrate(views, versionOf(raw)) : views);

  function defaults(cat: MdViewsCatalogue): MdView[] {
    const out: MdView[] = [];
    for (const seed of cat.defaults()) {
      const view = mdViewsSanitise(cat.id, seed);
      if (view) out.push(view);
    }
    return out;
  }

  function readDoc(cat: MdViewsCatalogue, scope: unknown): MdViewsDoc | null {
    const raw = g.mdRead(keyFor(cat, scope), null);
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
    if (!Array.isArray(raw.views)) return null;
    const out: MdView[] = [];
    const seen = new Set<string>();
    for (const item of raw.views.slice(0, MAX_VIEWS)) {
      const view = mdViewsSanitise(cat.id, item);
      if (!view || seen.has(view.id)) continue;
      seen.add(view.id);
      out.push(view);
    }
    /* A stored document with no usable views is not an instruction to show an
       empty screen — it is a document that has lost its contents. */
    if (!out.length) return null;
    return { views: carried(cat, out, raw),
      activeId: typeof raw.activeId === 'string' ? raw.activeId : null };
  }

  function writeDoc(cat: MdViewsCatalogue, scope: unknown, doc: MdViewsDoc): boolean {
    const stamp = typeof cat.version === 'number' ? { v: cat.version } : {};
    return g.mdWrite(keyFor(cat, scope),
      { ...stamp, views: doc.views.slice(0, MAX_VIEWS), activeId: doc.activeId, at: Date.now() });
  }

  function mdViewsList(catId: unknown, scope?: unknown): MdView[] {
    const cat = mdViewsCatalogue(catId);
    if (!cat) return [];
    const doc = readDoc(cat, scope);
    return doc ? doc.views : defaults(cat);
  }

  function mdViewsById(catId: unknown, id: unknown, scope?: unknown): MdView | null {
    return mdViewsList(catId, scope).find((view) => view.id === id) || null;
  }

  /** The view a screen opens on. Falls back to the first rather than to
      nothing, because an active id pointing at a deleted view is a blank
      screen that looks exactly like a broken one. */
  function mdViewsActiveId(catId: unknown, scope?: unknown): string {
    const views = mdViewsList(catId, scope);
    if (!views.length) return '';
    const cat = mdViewsCatalogue(catId)!;
    const doc = readDoc(cat, scope);
    const wanted = doc && doc.activeId;
    return wanted && views.some((view) => view.id === wanted) ? wanted : views[0]!.id;
  }

  /* Which view you are looking at is not something you DID, so ⌘Z must never
     merely move you back to it — a press that only switched the view would
     look like a press that did nothing, and the act it was meant to take back
     would still be there.

     It needs saying out loud because the pointer lives in the same document as
     the views themselves, and that document is inside a window's undo. What
     remains, deliberately: undoing an act that itself changed this document —
     creating, editing or deleting a view — also puts back the view that was
     active then. Separating the pointer from the views is a storage change. */
  function mdViewsSetActive(catId: unknown, id: unknown, scope?: unknown): boolean {
    const cat = mdViewsCatalogue(catId);
    if (!cat) return false;
    const views = mdViewsList(catId, scope);
    if (!views.some((view) => view.id === id)) return false;
    const write = () => writeDoc(cat, scope, { views, activeId: id as string });
    return typeof g.mdHistoryQuiet === 'function' ? g.mdHistoryQuiet(write) : write();
  }

  /* One Save, one write: the extras — filters, description, display — ride the
     same sanitiser as everything stored, and the words that name the view (id,
     type, name) are said LAST, so nothing can wear them in from the extras. */
  function mdViewsCreate(catId: unknown, type: unknown, name?: unknown,
    extras?: unknown, scope?: unknown): MdView | null {
    const cat = mdViewsCatalogue(catId);
    if (!cat) return null;
    const views = mdViewsList(catId, scope);
    if (views.length >= MAX_VIEWS) return null;
    const base = extras && typeof extras === 'object' && !Array.isArray(extras)
      ? extras as Record<string, unknown> : {};
    const made = mdViewsSanitise(catId, { ...base, id: newId(), type, name });
    if (!made) return null;
    return writeDoc(cat, scope, { views: [...views, made], activeId: made.id }) ? made : null;
  }

  /** Change part of one view. The patch goes through the sanitiser with the
      rest of the view, so a nonsense value falls back to the field's default
      rather than being stored and read back as nonsense for ever. */
  function mdViewsPatch(catId: unknown, id: unknown, patch: unknown, scope?: unknown): boolean {
    const cat = mdViewsCatalogue(catId);
    if (!cat) return false;
    if (!patch || typeof patch !== 'object') return false;
    const views = mdViewsList(catId, scope);
    const at = views.findIndex((view) => view.id === id);
    if (at === -1) return false;
    const next = mdViewsSanitise(catId, { ...views[at], ...(patch as object), id: views[at]!.id });
    if (!next) return false;
    /* A patch that changes nothing writes nothing. writeDoc stamps the doc
       with the hour it was written, so without this guard every no-op patch
       — a Reset pressed twice, a toggle toggled back — would produce a
       byte-DIFFERENT document, bump the storage revision, invalidate every
       memo keyed on it, and ask the other devices to pull a nothing. */
    if (JSON.stringify(next) === JSON.stringify(views[at])) return true;
    const out = [...views];
    out[at] = next;
    const doc = readDoc(cat, scope);
    return writeDoc(cat, scope, { views: out, activeId: (doc && doc.activeId) || views[0]!.id });
  }

  function mdViewsDelete(catId: unknown, id: unknown, scope?: unknown): boolean {
    const cat = mdViewsCatalogue(catId);
    if (!cat) return false;
    const views = mdViewsList(catId, scope);
    if (views.length <= 1) return false;              // see the header
    const rest = views.filter((view) => view.id !== id);
    if (rest.length === views.length) return false;
    const doc = readDoc(cat, scope);
    const active = doc && doc.activeId;
    return writeDoc(cat, scope, { views: rest, activeId: active && active !== id ? active : rest[0]!.id });
  }

  /* Absent until somebody changes something. The defaults that ship are code,
     and code does not need syncing. */
  function mdViewsSyncDocs(catId: unknown, scopes?: unknown): Record<string, unknown> {
    const cat = mdViewsCatalogue(catId);
    if (!cat) return {};
    const list = Array.isArray(scopes) ? scopes : [undefined];
    const out: Record<string, unknown> = {};
    for (const scope of list) {
      const doc = readDoc(cat, scope);
      if (doc) out[docFor(cat, scope)] = typeof cat.version === 'number'
        ? { v: cat.version, views: doc.views, activeId: doc.activeId }
        : { views: doc.views, activeId: doc.activeId };
    }
    return out;
  }

  function mdViewsApplyDoc(catId: unknown, key: unknown, value: unknown): boolean {
    const cat = mdViewsCatalogue(catId);
    if (!cat || typeof key !== 'string') return false;
    let scope: string | undefined;
    if (key === cat.doc) scope = undefined;
    else if (key.startsWith(`${cat.doc}:`)) {
      const held = scopeOf(key.slice(cat.doc.length + 1));
      if (!held) return false;
      scope = held;
    } else return false;
    if (value == null) return g.mdRemove(keyFor(cat, scope));
    if (typeof value !== 'object' || Array.isArray(value)) return false;
    const raw = value as Record<string, any>;
    if (!Array.isArray(raw.views)) return false;
    const out: MdView[] = [];
    const seen = new Set<string>();
    for (const item of raw.views.slice(0, MAX_VIEWS)) {
      const view = mdViewsSanitise(catId, item);
      if (!view || seen.has(view.id)) continue;
      seen.add(view.id);
      out.push(view);
    }
    return writeDoc(cat, scope,
      { views: carried(cat, out, raw),
        activeId: typeof raw.activeId === 'string' ? raw.activeId : null });
  }

  /* ═══ the row pipeline ════════════════════════════════════════════════════
     Filter → sort → group. Pure: no DOM, no React, and no rendering decision
     anywhere in it, so every rule below can be argued about on its own and
     tested without a screen. */

  const asText = (value: unknown): string =>
    value == null ? '' : String(value).toLocaleLowerCase();

  /* An array value under `is` is "is any of", under `is_not` "is none of".
     The empty cell keeps its scalar meaning: an any-of never claims a row
     that is not saying, and a none-of never excludes one. `''` in the array
     cannot smuggle the empty cell in — the `!empty` guard runs first. */
  const anyOf = (cell: unknown, value: unknown[]): boolean =>
    value.some((one) => String(cell) === String(one));

  function passes(cell: unknown, filter: MdViewFilter): boolean {
    const empty = cell == null || cell === '';
    switch (filter.operator) {
      case 'empty': return empty;
      case 'not_empty': return !empty;
      case 'is': return !empty && (Array.isArray(filter.value)
        ? anyOf(cell, filter.value) : String(cell) === String(filter.value));
      case 'is_not': return empty || (Array.isArray(filter.value)
        ? !anyOf(cell, filter.value) : String(cell) !== String(filter.value));
      case 'contains': return !empty && asText(cell).includes(asText(filter.value));
      case 'not_contains': return empty || !asText(cell).includes(asText(filter.value));
      /* A date comparison on a row with no date is not TRUE and not FALSE —
         it is a question about a plan nobody made. It is answered false, and
         `empty` is how somebody asks for those rows instead. */
      case 'before': return !empty && String(cell) < String(filter.value);
      case 'after': return !empty && String(cell) > String(filter.value);
      case 'on_or_before': return !empty && String(cell) <= String(filter.value);
      case 'on_or_after': return !empty && String(cell) >= String(filter.value);
      default: return true;
    }
  }

  /** Two cells of the same field, ordered. Returns 0 for equal; the caller
      applies the direction, and applies it only when neither side is empty. */
  function compare(cat: MdViewsCatalogue, a: unknown, b: unknown, field: string): number {
    /* A ladder can be a FUNCTION, because a vocabulary somebody writes is not
       known when a catalogue registers itself. Asked per comparison, which is
       the same cost as the array lookup it replaces plus one call. */
    const declared = cat.declared && cat.declared[field];
    const ladder = typeof declared === 'function' ? declared() : declared;
    if (ladder) return ladder.indexOf(String(a)) - ladder.indexOf(String(b));
    if (cat.numeric && cat.numeric.indexOf(field) !== -1) return Number(a) - Number(b);
    return String(a).localeCompare(String(b));
  }

  const searchOf = (cat: MdViewsCatalogue, row: any): string =>
    (cat.search ? cat.search(row) : row.name) || '';

  function filterRows(cat: MdViewsCatalogue, rows: any[], view: MdView): any[] {
    const needle = view.search.trim().toLocaleLowerCase();
    /* The lens, ahead of the questions and outside their join. Built once: a
       hundred `includes` per row over two thousand rows is a walk nobody asked
       for. A catalogue with no `lensOf` has no lens — a `scope` stored on one
       of its views is carried, never obeyed. */
    const lens = view.scope && cat.lensOf ? new Set(view.scope) : null;
    return rows.filter((row) => {
      if (lens && !lens.has(cat.lensOf!(row))) return false;
      if (cat.hidden && cat.hidden(row, view)) return false;
      if (needle && !searchOf(cat, row).toLocaleLowerCase().includes(needle)) return false;
      /* No filters is no filter, whichever word is on the toggle: `[].some()`
         is false, and a view set to `any` with nothing in it would answer a
         question nobody asked with an empty screen. */
      if (!view.filters.length) return true;
      const match = (filter: MdViewFilter) => passes(cat.cell(row, filter.field), filter);
      return view.conjunction === 'or' ? view.filters.some(match) : view.filters.every(match);
    });
  }

  function sortRows(cat: MdViewsCatalogue, rows: any[], view: MdView): any[] {
    const out = rows.slice();
    /* The hand speaks only when no sort does: a view with sorts obeys them and
       the hand-made order waits underneath, exactly where it was left. Rows
       the order does not list rank Infinity together and fall through to the
       creation-order tiebreak — which is also the whole answer when the order
       is stale: dead ids match no row, and nothing breaks. */
    const manual = !view.sorts.length && view.order.length
      ? new Map(view.order.map((id, at) => [id, at])) : null;
    out.sort((left, right) => {
      if (manual) {
        const a = manual.has(left.id) ? manual.get(left.id)! : Infinity;
        const b = manual.has(right.id) ? manual.get(right.id)! : Infinity;
        if (a !== b) return a - b;
      }
      for (const sort of view.sorts) {
        const a = cat.cell(left, sort.field), b = cat.cell(right, sort.field);
        const aEmpty = a == null || a === '', bEmpty = b == null || b === '';
        if (aEmpty && bEmpty) continue;
        // Empties last, whichever way the arrow points. See the header.
        if (aEmpty || bEmpty) return aEmpty ? 1 : -1;
        const ranked = cat.rank && cat.rank[sort.field];
        const compared = ranked ? ranked(left) - ranked(right)
          : compare(cat, a, b, sort.field);
        if (compared) return sort.direction === 'desc' ? -compared : compared;
      }
      /* The order they were made in, so a list with no sort — or a tie under
         one — never shuffles under somebody between two renders. */
      return left.at - right.at || String(left.id).localeCompare(String(right.id));
    });
    return out;
  }

  /* Memoised on (this catalogue, this scope, this view, this ctx, storage). A
     screen renders many times while somebody drags a card, and filtering and
     sorting two thousand rows eight times to draw the same list once is the
     defect md-database.ts already found and fixed. mdStorageRevision() is what
     makes a write show up immediately; todayKey and nowMin are in the key
     because TIME PASSING IS NOT A WRITE — a record scheduled for 14:00 becomes
     time given at 14:45 with nothing stored. */
  const rowsMemo = new Map<string, any[]>();
  let rowsMemoAt: number | null = null;

  /* The ctx's parts are identified by OBJECT IDENTITY, not by anything read
     out of them. React hands this the same objects until something changes
     them, so identity is exact and free — where counting them would be a walk
     on every call, and would answer the same for two different maps that
     happened to hold the same number of things. */
  const ctxSerial = new WeakMap<object, number>();
  let ctxSerialNext = 0;
  function serialOf(value: unknown): number {
    if (!value || typeof value !== 'object') return -1;
    let held = ctxSerial.get(value as object);
    if (held === undefined) { held = (ctxSerialNext += 1); ctxSerial.set(value as object, held); }
    return held;
  }

  function memoKey(cat: MdViewsCatalogue, view: MdView, ctx: any): string | null {
    if (typeof g.mdStorageRevision !== 'function') return null;
    const todayKey = (ctx && ctx.todayKey) || '';
    const nowMin = (ctx && ctx.nowMin) || 0;
    /* The scope rides in as whatever the catalogue's own memo hook names —
       a scoped catalogue whose rows depend on which scope is being asked
       must say so there, because this file cannot know. Which is why a memo
       part that is a plain STRING has to count for itself rather than be
       identified: a project id is not an object, and giving every id the same
       serial would hand one project's board to the next one. */
    const parts = (cat.memo ? cat.memo(ctx) : [])
      .map((part) => (part && typeof part === 'object' ? `#${serialOf(part)}` : `=${String(part)}`))
      .join(',');
    return `${cat.id} ${todayKey} ${nowMin} ${parts} `
      + JSON.stringify([view.scope, view.filters, view.conjunction, view.sorts, view.order,
        view.showClosed, view.search]);
  }

  /** Every row this view holds, filtered and sorted. */
  function mdViewsRows(catId: unknown, viewValue: unknown, ctx?: any): any[] {
    const cat = mdViewsCatalogue(catId);
    if (!cat) return [];
    const view = mdViewsSanitise(catId, viewValue) || mdViewsSanitise(catId, { type: 'list' })!;
    const key = memoKey(cat, view, ctx);
    if (key === null) return sortRows(cat, filterRows(cat, cat.rows(ctx), view), view);
    const revision = g.mdStorageRevision();
    if (rowsMemoAt !== revision) { rowsMemo.clear(); rowsMemoAt = revision; }
    let held = rowsMemo.get(key);
    if (!held) { held = sortRows(cat, filterRows(cat, cat.rows(ctx), view), view); rowsMemo.set(key, held); }
    /* A copy on the way out: the surface hands this straight to React, and a
       caller that spliced it in place would be editing what every later reader
       sees. The rows inside are shared and never written to. */
    return held.slice();
  }

  /** The picker's arithmetic: for ONE field, how many rows each choice would
      show — keyed by the choice's id, with `empty` for the cell that is not
      saying. Computed by the same pipeline that draws the rows, so the number
      beside a choice and the list behind the menu can never disagree.

      Under `and` the tally is conditioned on the view's OTHER filters (never
      the field's own: the picker counts what choosing an option WOULD show,
      not what the current answer already lets through). Under `or` a choice
      admits its rows regardless of the other filters, so the honest count is
      the plain tally. Search and closed-visibility ride along in both cases,
      because they are part of the question whichever word joins it. */
  function mdViewsFacetCounts(catId: unknown, viewValue: unknown, ctx: any,
    fieldValue: unknown): Record<string, number> {
    const cat = mdViewsCatalogue(catId);
    const out: Record<string, number> = {};
    if (!cat) return out;
    const view = mdViewsSanitise(catId, viewValue) || mdViewsSanitise(catId, { type: 'list' })!;
    const field = typeof fieldValue === 'string' && cat.fields.indexOf(fieldValue) !== -1
      ? fieldValue : null;
    if (!field) return out;
    const others = view.conjunction === 'or' ? []
      : view.filters.filter((filter) => filter.field !== field);
    const rows = mdViewsRows(catId, { ...view, filters: others, sorts: [], order: [] }, ctx);
    for (const row of rows) {
      const cell = cat.cell(row, field);
      const key = cell == null || cell === '' ? 'empty' : String(cell);
      out[key] = (out[key] || 0) + 1;
    }
    return out;
  }

  interface MdViewsGroup { id: string; label: string; rows: any[] }

  /** The buckets of a grouping axis, IN ORDER, every one of them present
      whether or not anything is in it — see the header. */
  function mdViewsGroups(catId: unknown, viewValue: unknown, ctx?: any): MdViewsGroup[] {
    const cat = mdViewsCatalogue(catId);
    if (!cat) return [];
    const view = mdViewsSanitise(catId, viewValue) || mdViewsSanitise(catId, { type: 'list' })!;
    const rows = mdViewsRows(catId, view, ctx);
    if (!view.groupBy) return [{ id: 'all', label: 'All', rows }];

    const map = new Map<string, any[]>();
    const labels = new Map<string, string>();
    /* The lens is the one thing allowed to take a bucket away, and only on its
       OWN axis: a column for something somebody has just set aside is the
       broken promise inverted — a place nothing can ever land. Every other
       axis is untouched by it, because those are the app's opinion about a
       life rather than the lens's, and `showEmptyGroups` already governs
       them. */
    const onLensAxis = !!view.scope && !!cat.lensAxis && view.groupBy === cat.lensAxis;
    const lens = onLensAxis ? new Set(view.scope!) : null;
    for (const bucket of cat.buckets(view.groupBy, rows, ctx)) {
      if (lens && !lens.has(bucket.id)) continue;
      map.set(bucket.id, []); labels.set(bucket.id, bucket.label);
    }
    /* "Under none of them" is a real place, so it is a real column — unless
       the lens is pointed somewhere that is not it, or the axis has no such
       place at all. An axis that names no empty label is saying its cell is
       never absent, and a column nothing can ever land in is worse than no
       column. */
    const emptyLabel = cat.emptyLabel && cat.emptyLabel[view.groupBy];
    if (emptyLabel && (!lens || lens.has('empty'))) {
      map.set('empty', []);
      labels.set('empty', emptyLabel);
    }

    for (const row of rows) {
      const said = cat.bucketOf ? cat.bucketOf(row, view.groupBy, rows) : undefined;
      const raw = said === undefined ? cat.cell(row, view.groupBy) : said;
      const key = raw == null || raw === '' ? 'empty' : String(raw);
      /* A value the axis did not declare still gets a home — a pursuit deleted
         between the read and the group, say. Losing the row would be worse than
         an extra column. */
      if (!map.has(key)) { map.set(key, []); labels.set(key, String(raw)); }
      map.get(key)!.push(row);
    }

    const out: MdViewsGroup[] = [];
    for (const [id, groupRows] of map) {
      if (!view.showEmptyGroups && !groupRows.length) continue;
      out.push({ id, label: labels.get(id) || id, rows: groupRows });
    }
    /* Insertion order is the axis's own order, which is the point — the empty
       bucket was inserted last and stays last. Only the direction flips, and
       the empty bucket sits out of the flip. */
    if (view.groupDirection === 'desc') {
      const empty = out.filter((group) => group.id === 'empty');
      return [...out.filter((group) => group.id !== 'empty').reverse(), ...empty];
    }
    return out;
  }

  g.MD_VIEWS_TYPES = VIEW_TYPES;
  g.MD_VIEWS_ZOOMS = ZOOMS;
  g.MD_VIEWS_OPERATORS = OPERATORS;
  g.MD_VIEWS_MAX_VIEWS = MAX_VIEWS;
  g.MD_VIEWS_MAX_SORTS = MAX_SORTS;
  Object.assign(g, { mdViewsRegister, mdViewsCatalogue, mdViewsSanitise, mdViewsList,
    mdViewsById, mdViewsActiveId, mdViewsSetActive, mdViewsCreate, mdViewsPatch,
    mdViewsDelete, mdViewsSyncDocs, mdViewsApplyDoc, mdViewsRows, mdViewsGroups,
    mdViewsFacetCounts });
})(typeof window !== 'undefined' ? window : globalThis);
